import React from "react";
import styled from "styled-components";
import { router } from "@inertiajs/react";
import {
  saveOrderToLocalHistory,
  setStoredWebPushSubscriptionId,
} from "../../../../util";

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #f7f7f7 45%, #f4f4f4 100%);
  padding: 20px 16px 28px;
`;

const Wrapper = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  padding: 20px 20px 18px;
  margin-bottom: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.03);
`;

const Title = styled.h1`
  font-size: 24px;
  margin: 0 0 8px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
`;

const StatusLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(props) => props.$color || "#5f5f5f"};
  background: ${(props) => props.$background || "#f1f1f1"};
`;

const TotalRow = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ececec;
  display: flex;
  align-items: end;
  justify-content: space-between;
`;

const TotalLabel = styled.div`
  font-size: 13px;
  color: #666;
`;

const TotalValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #c9a961;
`;

const ActionRow = styled.div`
  margin-top: 14px;
`;

const Button = styled.a`
  display: inline-block;
  padding: 11px 16px;
  border-radius: 10px;
  background: #c9a961;
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #b69352;
    box-shadow: 0 6px 16px rgba(201, 169, 97, 0.35);
  }
`;

const BackButton = styled.button`
  border: none;
  background: transparent;
  color: #555;
  cursor: pointer;
  margin-bottom: 10px;
  padding: 4px 0;
  font-size: 14px;
  transition: color 0.2s ease;

  &:hover {
    color: #222;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 14px;
  font-size: 28px;
  font-weight: 600;
  color: #1f1f1f;
`;

const BodySectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 600;
  color: #222;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const ListItem = styled.li`
  border: 1px solid #efefef;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fcfcfc;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const ItemHeadline = styled.div`
  font-size: 15px;
  color: #2b2b2b;
  font-weight: 500;
`;

const ItemNote = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #767676;
`;

const EmptyState = styled.div`
  color: #888;
  font-size: 14px;
`;

export default function OrderShowPage(props) {
  const { building_code, order_id, web_push_vapid_public_key } = props;
  const [order, setOrder] = React.useState(null);
  const [hasAutoOpenedPayment, setHasAutoOpenedPayment] = React.useState(false);

  const loadOrder = React.useCallback(async () => {
    const response = await fetch(`/api/v1/orders/${order_id}`, {
      headers: { Accept: "application/json" },
    });
    const payload = await response.json();
    setOrder(payload?.data || null);
  }, [order_id]);

  React.useEffect(() => {
    loadOrder();
    const interval = setInterval(loadOrder, 10000);
    return () => clearInterval(interval);
  }, [loadOrder]);

  React.useEffect(() => {
    if (!order?.id) return;
    saveOrderToLocalHistory({ buildingCode: building_code, order });
  }, [order, building_code]);

  React.useEffect(() => {
    if (hasAutoOpenedPayment || !order?.payment_link_url) return;

    const queryParams = new URLSearchParams(window.location.search);
    const shouldAutoPay = queryParams.get("auto_pay") === "1";
    if (!shouldAutoPay) return;

    const continueToPayment = async () => {
      setHasAutoOpenedPayment(true);
      await ensurePushSubscriptionIdStored();
      window.location.assign(order.payment_link_url);
    };

    continueToPayment();
  }, [order, hasAutoOpenedPayment]);

  const ensurePushSubscriptionIdStored = async () => {
    if (!web_push_vapid_public_key) return;
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;

    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") return;

    const registration = await navigator.serviceWorker.register("/service-worker.js");
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(web_push_vapid_public_key),
      });
    }

    const json = subscription.toJSON();
    const response = await fetch("/api/v1/web_push_subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        building_code: building_code,
        endpoint: json.endpoint,
        p256dh_key: json.keys?.p256dh,
        auth_key: json.keys?.auth,
      }),
    });
    const payload = await response.json();
    const id = payload?.data?.id ?? null;
    if (id) setStoredWebPushSubscriptionId(building_code, id);
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const renderStatusBadge = (type, value) => {
    const normalized = String(value || "-").toLowerCase();

    if (type === "payment") {
      if (normalized === "paid") {
        return <StatusBadge $color="#0f7a39" $background="#dff7e8">Paid</StatusBadge>;
      }
      if (normalized === "pending") {
        return <StatusBadge $color="#9a6a00" $background="#fff4da">Pending</StatusBadge>;
      }
      if (normalized === "expired" || normalized === "failed" || normalized === "canceled") {
        return <StatusBadge $color="#a1152d" $background="#ffe3e8">{value}</StatusBadge>;
      }
      return <StatusBadge>{value || "-"}</StatusBadge>;
    }

    if (type === "delivery") {
      if (normalized === "on_progress") {
        return <StatusBadge $color="#155d9b" $background="#e4f2ff">On Progress</StatusBadge>;
      }
      if (normalized === "finished") {
        return <StatusBadge $color="#0f7a39" $background="#dff7e8">Finished</StatusBadge>;
      }
      if (normalized === "pending_payment") {
        return <StatusBadge $color="#9a6a00" $background="#fff4da">Pending Payment</StatusBadge>;
      }
      return <StatusBadge>{value || "-"}</StatusBadge>;
    }

    return <StatusBadge>{value || "-"}</StatusBadge>;
  };

  return (
    <Container>
      <Wrapper>
        <BackButton onClick={() => router.visit(`/hebe/${building_code}/hgs/orders`)}>
          Back to my orders
        </BackButton>

        {!order ? (
          <Card>Loading order...</Card>
        ) : (
          <>
            <Card>
              <SectionTitle>{order.order_number}</SectionTitle>
              <StatusRow>
                <StatusLabel>Payment status</StatusLabel>
                {renderStatusBadge("payment", order.payment_status)}
              </StatusRow>
              <StatusRow>
                <StatusLabel>Delivery status</StatusLabel>
                {renderStatusBadge("delivery", order.delivery_status)}
              </StatusRow>
              <TotalRow>
                <TotalLabel>Total amount</TotalLabel>
                <TotalValue>{order.total}</TotalValue>
              </TotalRow>
              {order.payment_link_url ? (
                <ActionRow>
                  <Button href={order.payment_link_url} target="_blank" rel="noreferrer">
                    Open Payment Link
                  </Button>
                </ActionRow>
              ) : null}
            </Card>

            <Card>
              <BodySectionTitle>Order Items</BodySectionTitle>
              <List>
                {(order.lines || []).map((line) => (
                  <ListItem key={line.id}>
                    <ItemHeadline>
                      {line.name} x {line.quantity}
                    </ItemHeadline>
                    {line.note ? <ItemNote>Note: {line.note}</ItemNote> : null}
                  </ListItem>
                ))}
              </List>
            </Card>

            <Card>
              <BodySectionTitle>Notifications</BodySectionTitle>
              {(order.notifications || []).length === 0 ? (
                <EmptyState>No notifications yet.</EmptyState>
              ) : (
                <List>
                  {order.notifications.map((notification) => (
                    <ListItem key={notification.id}>
                      <ItemHeadline>{notification.title}</ItemHeadline>
                      <ItemNote>{notification.message}</ItemNote>
                    </ListItem>
                  ))}
                </List>
              )}
            </Card>
          </>
        )}
      </Wrapper>
    </Container>
  );
}
